import { getProvider } from "@aux0/connectors";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { parseAsBoolean, parseAsString, useQueryState } from "nuqs";
import { AppLink } from "@/components/common/app-link";
import { ProviderMark, Tag } from "@/components/common/badges";
import { SearchFilter } from "@/components/common/filters";
import { Page, PageTitle, Panel } from "@/components/common/page";
import { Stat } from "@/components/common/stat";
import { EmptyState, PageSkeleton } from "@/components/common/states";
import { q } from "@/data/queries";
import { formatNumber, relativeTime } from "@/lib/format";
import { projectPath } from "@/lib/nav";

/**
 * Contacts.
 *
 * The old People screen read the social model — LinkedIn profiles,
 * invitations, posts — which belongs to the deferred channels and is now
 * behind `features.messaging`. This is the launch-scope screen: address books
 * from Google Contacts, Outlook and CardDAV.
 *
 * The one distinction the screen exists to make is `inferred`. Aux0 derives
 * addresses from message headers, which is what makes autocomplete useful and
 * what must never be written back to someone's address book. The toggle is off
 * by default and says what it does.
 */
export const Route = createFileRoute("/p/$project/contacts/")({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(
        q.contacts({ limit: 200, includeInferred: true }),
      ),
      context.queryClient.ensureQueryData(q.contactGroups()),
    ]),
  pendingComponent: PageSkeleton,
  component: ContactsPage,
});

function ContactsPage() {
  const { project } = Route.useParams();
  const { data: all } = useSuspenseQuery(
    q.contacts({ limit: 200, includeInferred: true }),
  );
  const { data: groups } = useSuspenseQuery(q.contactGroups());

  const [search] = useQueryState("q", parseAsString);
  const [group, setGroup] = useQueryState("group", parseAsString);
  const [inferred, setInferred] = useQueryState(
    "inferred",
    parseAsBoolean.withDefault(false).withOptions({ history: "replace" }),
  );

  const needle = search?.trim().toLowerCase() ?? "";
  const shown = all.items.filter((contact) => {
    if (!inferred && contact.inferred) return false;
    if (group && !contact.groupIds.includes(group)) return false;
    if (!needle) return true;
    return (
      contact.name.toLowerCase().includes(needle) ||
      contact.emails.some((email) => email.address.toLowerCase().includes(needle)) ||
      contact.organizations.some((org) => org.name.toLowerCase().includes(needle))
    );
  });

  const filtered = Boolean(needle || group);
  const inferredCount = all.items.filter((contact) => contact.inferred).length;

  return (
    <Page wide>
      <PageTitle
        title="Contacts"
        description="Address books from the accounts your users have linked, plus the addresses Aux0 derived from their mail. Reads come from the connected account's own view, so what you can see is what that person can see."
      />

      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border lg:grid-cols-4">
        <Stat
          label="In an address book"
          value={formatNumber(all.items.length - inferredCount)}
          footnote="Synced from a provider"
        />
        <Stat
          label="Derived from mail"
          value={formatNumber(inferredCount)}
          footnote="Autocomplete only, never written back"
        />
        <Stat label="Groups" value={groups.length} footnote="Labels and categories" />
        <Stat
          label="Reported by the providers"
          value={formatNumber(
            groups
              .filter((candidate) => candidate.system)
              .reduce((sum, candidate) => sum + candidate.count, 0),
          )}
          footnote="Across every connected address book"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchFilter placeholder="Name, address or company" />
        <div className="flex flex-wrap items-center gap-1">
          <button
            type="button"
            onClick={() => void setGroup(null)}
            aria-pressed={!group}
            className="h-8 rounded-md border border-dashed px-2.5 text-[12.5px] transition-colors hover:bg-accent/60 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none aria-pressed:border-solid aria-pressed:bg-accent/60 aria-pressed:font-medium"
          >
            All groups
          </button>
          {groups.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              onClick={() => void setGroup(candidate.id)}
              aria-pressed={group === candidate.id}
              className="h-8 rounded-md border border-dashed px-2.5 text-[12.5px] transition-colors hover:bg-accent/60 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none aria-pressed:border-solid aria-pressed:bg-accent/60 aria-pressed:font-medium"
            >
              {candidate.name}
            </button>
          ))}
        </div>
        {/* A pressed chip rather than a switch, so it reads as the same kind
            of control as the group filters beside it and keeps its affordance
            in both themes. */}
        <button
          type="button"
          onClick={() => void setInferred(!inferred)}
          aria-pressed={inferred}
          className="ml-auto h-8 shrink-0 rounded-md border border-dashed px-2.5 text-[12.5px] transition-colors hover:bg-accent/60 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none aria-pressed:border-solid aria-pressed:border-wait/40 aria-pressed:bg-wait-soft/60 aria-pressed:font-medium aria-pressed:text-wait"
        >
          {inferred ? "Derived addresses shown" : "Show derived addresses"}
        </button>
      </div>

      <Panel
        title={`Contacts (${formatNumber(shown.length)})`}
        description="One row per person, with every address the provider holds"
        bodyClassName="divide-y"
      >
        {shown.length === 0 ? (
          <EmptyState
            title={filtered ? "No contact matches that" : "No contacts yet"}
            description={
              filtered
                ? "Clear the search or pick another group."
                : "Connect a Google Contacts, Outlook or CardDAV account and its address book appears here."
            }
            action={
              filtered ? (
                <button
                  type="button"
                  onClick={() => void setGroup(null)}
                  className="text-[13px] font-medium text-brand hover:underline"
                >
                  Clear the group filter
                </button>
              ) : undefined
            }
          />
        ) : (
          shown.map((contact) => (
            <AppLink
              key={contact.id}
              to={projectPath(project, `/contacts/${contact.id}`)}
              className="flex flex-wrap items-start gap-3 px-4 py-3 transition-colors hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
            >
              <ProviderMark provider={contact.provider} className="mt-0.5" />
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-[13px] font-medium">
                    {contact.name}
                  </span>
                  {contact.starred ? <Tag mono>starred</Tag> : null}
                  {contact.inferred ? (
                    <Tag
                      mono
                      title="Seen in a message, never in an address book. Autocomplete only."
                      className="border-wait/30 bg-wait-soft/60 text-wait"
                    >
                      derived
                    </Tag>
                  ) : null}
                  {contact.emails.length > 1 ? (
                    <Tag mono>{contact.emails.length} addresses</Tag>
                  ) : null}
                </span>
                <span className="mt-0.5 block truncate text-[12.5px] text-muted-foreground">
                  {contact.emails.find((email) => email.primary)?.address ??
                    contact.emails[0]?.address ??
                    "No address"}
                </span>
                {contact.organizations[0] ? (
                  <span className="mt-0.5 block truncate text-[11.5px] text-ink-faint">
                    {contact.organizations[0].title
                      ? `${contact.organizations[0].title} · `
                      : ""}
                    {contact.organizations[0].name}
                  </span>
                ) : null}
              </span>
              <span className="hidden shrink-0 text-right text-[11px] text-ink-faint sm:block">
                <span className="block">
                  {getProvider(contact.provider)?.name ?? contact.provider}
                </span>
                <span className="block">updated {relativeTime(contact.updatedAt)}</span>
              </span>
            </AppLink>
          ))
        )}
      </Panel>
    </Page>
  );
}
